import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, CreditCard, ExternalLink, FileText, Download, Receipt, FlaskConical, Shield } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SubscriptionData {
  subscribed: boolean;
  product_id: string | null;
  subscription_end: string | null;
}

interface Invoice {
  id: string;
  number: string | null;
  status: string | null;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created: number;
  period_start: number;
  period_end: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

interface ElaraOrder {
  id: string;
  order_type: string;
  status: string;
  amount_cents: number;
  currency: string;
  created_at: string;
  batch_number: string | null;
  coa_document_url: string | null;
}

const BillingSettings = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionData>({
    subscribed: false,
    product_id: null,
    subscription_end: null,
  });
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [elaraOrders, setElaraOrders] = useState<ElaraOrder[]>([]);

  const fetchSubscription = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      if (data) setSubscription(data);
    } catch {
      // User may not have a subscription — that's fine
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("list-invoices");
      if (error) throw error;
      if (data?.invoices) setInvoices(data.invoices);
    } catch {
      // No invoices or not a Stripe customer — that's fine
    } finally {
      setInvoicesLoading(false);
    }
  };

  const fetchElaraOrders = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("orders")
      .select("id, order_type, status, amount_cents, currency, created_at, batch_number, coa_document_url")
      .eq("patient_id", user.id)
      .order("created_at", { ascending: false })
      .limit(50);
    setElaraOrders((data as ElaraOrder[]) || []);
  };

  useEffect(() => {
    fetchSubscription();
    fetchInvoices();
    fetchElaraOrders();
  }, [user]);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch {
      toast({
        title: "Error",
        description: "Could not open billing portal. You may not have an active subscription.",
        variant: "destructive",
      });
    } finally {
      setPortalLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const statusBadge = (status: string | null) => {
    switch (status) {
      case "paid":
        return <Badge variant="default" className="text-xs">Paid</Badge>;
      case "open":
        return <Badge variant="outline" className="text-xs border-accent text-accent-foreground">Open</Badge>;
      case "void":
        return <Badge variant="secondary" className="text-xs">Void</Badge>;
      case "uncollectible":
        return <Badge variant="destructive" className="text-xs">Uncollectible</Badge>;
      case "draft":
        return <Badge variant="secondary" className="text-xs">Draft</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">{status || "Unknown"}</Badge>;
    }
  };

  if (loading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const ordersWithBatch = elaraOrders.filter((o) => o.batch_number || o.coa_document_url);

  return (
    <div className="space-y-6">
      {/* Subscription Card */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Subscription & Billing</CardTitle>
          <CardDescription>View your plan details and manage billing.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current Plan */}
          <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
            <div className="space-y-1">
              <p className="font-medium text-foreground text-sm">Current Plan</p>
              <p className="text-sm text-muted-foreground">
                {subscription.subscribed ? "Active subscription" : "No active subscription"}
              </p>
            </div>
            <Badge variant={subscription.subscribed ? "default" : "secondary"}>
              {subscription.subscribed ? "Active" : "Free"}
            </Badge>
          </div>

          {/* Renewal Date */}
          {subscription.subscribed && subscription.subscription_end && (
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
              <div className="space-y-1">
                <p className="font-medium text-foreground text-sm">Next Renewal</p>
                <p className="text-sm text-muted-foreground">
                  {new Date(subscription.subscription_end).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>
          )}

          <Separator />

          {/* Actions */}
          <div className="space-y-3">
            {subscription.subscribed ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Manage your payment method, update your plan, or cancel your subscription through the billing portal.
                </p>
                <Button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  variant="outline"
                  className="gap-2"
                >
                  {portalLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <CreditCard size={14} />
                  )}
                  Manage Billing
                  <ExternalLink size={12} />
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  You're not currently subscribed to a plan. Subscribe to access consultations, personalized therapy plans, and ongoing support.
                </p>
                <Button
                  onClick={() => navigate("/customize")}
                  variant="default"
                  className="gap-2"
                >
                  View Plans
                </Button>
              </>
            )}
          </div>

          <Separator />

          {/* Refresh */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Subscription status is checked automatically. If you recently made changes, refresh below.
            </p>
            <Button variant="ghost" size="sm" onClick={() => { fetchSubscription(); fetchInvoices(); fetchElaraOrders(); }}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Batch & Quality Info Card */}
      {ordersWithBatch.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FlaskConical size={18} className="text-primary" />
              <CardTitle className="text-lg">Quality & Batch Information</CardTitle>
            </div>
            <CardDescription>
              Every compounded medication is tracked by batch/lot number with third-party Certificate of Analysis (COA) testing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {ordersWithBatch.map((order) => (
                <div key={order.id} className="flex items-start justify-between p-4 rounded-lg bg-muted/50 border border-border">
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium capitalize">{order.order_type} Order</p>
                      <Badge variant="secondary" className="text-xs">
                        {new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </Badge>
                    </div>
                    {order.batch_number && (
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Batch:</span> {order.batch_number}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {order.coa_document_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-xs"
                        onClick={() => window.open(order.coa_document_url!, "_blank")}
                      >
                        <Shield size={12} /> View COA
                        <ExternalLink size={10} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice History Card */}
      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Receipt size={18} className="text-primary" />
            <CardTitle className="text-lg">Invoice History</CardTitle>
          </div>
          <CardDescription>View and download your past invoices.</CardDescription>
        </CardHeader>
        <CardContent>
          {invoicesLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Receipt size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm">No invoices yet</p>
              <p className="text-xs mt-1">Invoices will appear here once you make a payment.</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {invoice.number || invoice.id.slice(0, 12) + "…"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(invoice.created)}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {formatCurrency(invoice.amount_due, invoice.currency)}
                      </TableCell>
                      <TableCell>
                        {statusBadge(invoice.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {invoice.hosted_invoice_url && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => window.open(invoice.hosted_invoice_url!, "_blank")}
                              title="View invoice"
                            >
                              <FileText size={14} />
                            </Button>
                          )}
                          {invoice.invoice_pdf && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => window.open(invoice.invoice_pdf!, "_blank")}
                              title="Download PDF"
                            >
                              <Download size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSettings;
